/**
 * The picker's model list, resolved server-side for the current caller.
 *
 * `GET /api/agent/models` applies the platform layer (hidden), the plan layer
 * (upsell) and the org layer (restricted) — see
 * docs/plans/2026-07-30_MODEL_ACCESS_GOVERNANCE.md. The browser cannot compute
 * any of that, which is why the picker stopped rendering from a hardcoded list.
 *
 * ── Failing open, deliberately ────────────────────────────────────────
 *
 * A failed fetch falls back to the full static catalog with everything marked
 * `allowed`. That is the right trade because **the picker is not the gate**:
 * `checkModelAllowed` refuses the turn server-side regardless, so the worst a
 * fallback can do is show a model that then errors on send. Failing closed
 * would instead leave a user with an EMPTY picker during any blip — unable to
 * work, with nothing explaining why.
 *
 * Fetched once per mount and cached per module: the list changes when an admin
 * edits policy, not while someone is picking a model.
 */
import { useEffect, useState } from 'react'
import { request, isSessionEndedError } from '@papan/api-client'
import { MODEL_CATALOG, DEFAULT_MODEL, PROVIDER_TABS, type ModelCatalogEntry } from '@papan/types'

/** Why a shown model cannot be selected. Absent ⇒ selectable. */
export type ModelAccessState = 'allowed' | 'upsell' | 'restricted'

export interface ResolvedModel extends ModelCatalogEntry {
  access: ModelAccessState
  /** Present on `upsell` when a purchasable plan carries the model. */
  upsellPlan?: { id: string; name: string }
  /**
   * Present on `upsell`: whether THIS caller holds `manage_billing:write`.
   * Resolved server-side so the capability has one authority — a member who
   * cannot buy anything is told to ask an admin instead of to upgrade.
   */
  canUpgrade?: boolean
  /** Present on `restricted` — drives the toast wording only. */
  restrictedBy?: 'role' | 'team' | 'user'
}

export interface ModelCatalogResult {
  models: ResolvedModel[]
  defaultModel: string | null
  /** True while the first fetch is in flight (the static fallback is showing). */
  loading: boolean
}

interface Payload {
  models: ResolvedModel[]
  providers: typeof PROVIDER_TABS
  defaultModel: string | null
}

/**
 * Which message explains a locked model, as an i18n key + params.
 *
 * Four cases, and the distinctions are the whole point:
 *  - `restricted` — a person to ask, not a purchase.
 *  - `upsell` with a plan and the right to buy — the actionable upgrade.
 *  - `upsell` with a plan but NO right to buy — "upgrade" is useless advice to
 *    someone who cannot; point them at an admin instead. `canUpgrade` is
 *    resolved server-side from `manage_billing`, not guessed here.
 *  - `upsell` with no plan — no purchasable plan carries this model, so there
 *    is nothing to name. Never invent a target: the plan name is a pricing
 *    claim, and naming an unbuyable plan sends the customer to a 409 checkout.
 *
 * Pure and exported so the branching is testable without rendering a popover.
 */
export function modelLockReason(
  model: Pick<ResolvedModel, 'access' | 'upsellPlan' | 'canUpgrade'>,
): { key: string; params?: Record<string, string> } | null {
  if (model.access === 'allowed') return null
  if (model.access === 'restricted') return { key: 'model.restricted' }
  if (!model.upsellPlan) return { key: 'model.notInPlan' }
  return {
    key: model.canUpgrade ? 'model.upgradeTo' : 'model.askAdminUpgrade',
    params: { plan: model.upsellPlan.name },
  }
}

/** The static catalog, shaped like a resolved payload. */
const FALLBACK: ResolvedModel[] = MODEL_CATALOG.map((m) => ({ ...m, access: 'allowed' as const }))

// Module-scoped so a second picker mounting (ChatPanel + HeroChat both render
// one) reuses the first fetch instead of re-requesting.
let cached: Payload | null = null
let inflight: Promise<Payload> | null = null

/**
 * Bumped by every invalidation. A fetch that started BEFORE the bump may not
 * write `cached`, because its payload predates the admin's edit.
 *
 * Without this, invalidating during an in-flight fetch restored the stale
 * answer: `invalidateModelCatalog` cleared `cached`, every subscriber called
 * `load()`, `load()` saw `cached === null` but `inflight !== null` and handed
 * back the SAME pre-edit promise, whose `.then` then wrote that pre-edit
 * payload straight back into the cache and pushed it into every mounted
 * picker. Net effect: the picker keeps offering the model just restricted, and
 * the module cache stays poisoned until the next invalidation — exactly the
 * failure the notify half was added to prevent, reachable whenever a save
 * lands while any picker is mid-fetch.
 */
let generation = 0

async function load(): Promise<Payload> {
  if (cached) return cached
  if (!inflight) {
    const startedAt = generation
    const p: Promise<Payload> = request<Payload>('/api/agent/models')
      .then((res) => {
        if (generation === startedAt) cached = res
        return res
      })
      .finally(() => {
        // Only clear `inflight` if it is still OURS. An invalidation nulls it
        // and a subscriber immediately installs a replacement; a retired
        // request's `finally` landing afterwards would wipe that live handle,
        // and the next picker to mount would see no cache and no inflight and
        // start a THIRD fetch for one policy edit.
        if (inflight === p) inflight = null
      })
    inflight = p
  }
  return inflight
}

/** Mounted pickers, so an admin edit can reach the ones already on screen. */
const subscribers = new Set<() => void>()

/**
 * Drop the cache AND refresh every mounted picker — call after an admin edit.
 *
 * The notify half is not belt-and-braces. Clearing `cached` alone only helps
 * the NEXT mount, and each `useModelCatalog` keeps its own `payload` in state
 * after the first fetch — so a picker that is already on screen never re-reads
 * the module cache. In this shell the chat panel stays mounted while the admin
 * is in Settings, which makes "already on screen" the normal case rather than
 * the edge one: verified in the browser, a cache-clear-only version left the
 * picker still offering a model the server had already started refusing.
 */
export function invalidateModelCatalog(): void {
  cached = null
  // Retire any fetch already in flight: its answer predates this edit, so it
  // must not be allowed to write `cached` when it lands. `inflight` is cleared
  // too, so the subscribers below start a genuinely new request rather than
  // re-awaiting the stale one.
  generation++
  inflight = null
  for (const notify of [...subscribers]) notify()
}

export function useModelCatalog(): ModelCatalogResult {
  const [payload, setPayload] = useState<Payload | null>(cached)
  const [loading, setLoading] = useState(!cached)

  useEffect(() => {
    let alive = true
    const fetchCatalog = () => {
      // Same generation test the module cache applies. Guarding only `cached`
      // left this half open: after an invalidation the picker has TWO live
      // chains (the retired request A and its replacement B), and if A lands
      // last its `setPayload` renders the pre-edit catalog — re-offering the
      // model the admin just restricted, with `cached` correct and nothing to
      // re-correct the component until a remount.
      const startedAt = generation
      load()
        .then((res) => { if (alive && generation === startedAt) setPayload(res) })
        // #1316: an answer dropped because its session ended (this picker mounted in the next session while the old
        // session's request was in flight) is asked again — never left on the "everything allowed" fallback
        .then(() => { if (alive) setLoading(false) }, (e) => {
          if (alive && isSessionEndedError(e)) { fetchCatalog(); return }   // still loading: the re-ask settles it
          if (alive) setLoading(false)   // else keep whatever is showing — see the header
        })
    }
    if (!cached) fetchCatalog()
    // Deliberately does NOT flip `loading` back on: a policy edit should
    // update the list in place, not blank a picker someone may have open.
    const onInvalidated = () => { if (alive) fetchCatalog() }
    subscribers.add(onInvalidated)
    return () => { alive = false; subscribers.delete(onInvalidated) }
  }, [])

  return {
    models: payload?.models ?? FALLBACK,
    defaultModel: payload?.defaultModel ?? DEFAULT_MODEL,
    loading,
  }
}
